import { Col, Row } from 'antd';
import classNames from 'classnames';
import { MenuInfo } from 'rc-menu/lib/interface';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import { MATCHING } from '@common/constants';
import { SortBy } from '@common/enums';
import { getOnTrendLabelFromProduct } from '@common/helpers/onTrendLabel';
import {
    matchingCheckSkipToken,
    useEquivalentProductMatching,
} from '@common/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import { useEquivalentProductMatchingSummaryAlerts } from '@common/hooks/useEquivalentProductMatching/useEquivalentProductMatchingSummaryAlerts';
import { useLazyEquivalentProductMatchingSummaryAlerts } from '@common/hooks/useEquivalentProductMatching/useLazyEquivalentProductMatchingSummaryAlerts';
import { ColorModel } from '@common/models';
import { getProductDescription } from '@common/utils';
import { CardItem, Filters, SortByMenu } from '@core/components';
import ConfirmationDialog from '@core/components/confirmation-dialog/ConfirmationDialog';
import {
    ConfirmationDialogWithBanner,
    DialogBanner,
} from '@core/components/confirmation-dialog/ConfirmationDialogWithBanner';
import { updateFiltersOptions } from '@core/components/filters/filterUtils';
import Pagination from '@core/components/pagination/Pagination';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import usePagination from '@core/hooks/usePagination';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import useUpdateWithHistory from '@core/hooks/useUpdateWithHistory';
import ImageUtils from '@core/utils/ImageUtils';
import useColors from '@ee-api/colors-api/useColors';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { selectColorSortBy, updateColorFilters, updateColorSortBy } from '@redux/slices/colorSlice';
import { resetRequestedColor, selectRequestedColor } from '@redux/slices/materialSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import VisualizeIcon from '../../../../../../public/assets/icons/visualize.svg';
import { getColorRoomvoSku, sortColors } from './colors.utils';
import ColorCard from './components/ColorCard/ColorCard';
import SlabViewer from './components/SlabViewer/SlabViewer';
import useFilteredColors from './useFilteredColors';

import './colors.scss';

export interface ColorItemProps {
    id: number;
    color: ColorModel;
}

export interface ColorProps {
    step: number;
    stepInfo: string;
    disabled?: boolean;
}

const Colors = ({ stepInfo, step, disabled = false }: ColorProps): JSX.Element => {
    const { t } = useTranslation();

    const sortBy = useAppSelector(selectColorSortBy);

    const dispatch = useAppDispatch();

    const [isSlabViewerVisible, setIsSlabViewerVisible] = useState(false);
    const [previewColor, setPreviewColor] = useState<ColorModel>();
    const [isFilterExpanded, setIsFilterExpanded] = useState(true);
    const [candidateColor, setCandidateColor] = useState<ColorModel>();
    const [isModalVisible, setIsModalVisible] = useState(false);

    const zipcode = useAppSelector(selectZipcode);
    const { colors } = useColors();
    const { filteredColors, colorFilters } = useFilteredColors();
    const { retailerFileSettings } = useRetailerSettings();
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedColor } = useSelectedColor();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { retailerSettings } = useRetailerSettings();

    const { setSelectedColorWithHistory } = useUpdateWithHistory();

    const { replaceProductsWithEquivalents } = useEquivalentProductMatching();
    const { LoaderOnAlerts, replaceIfNoAlerts } = useLazyEquivalentProductMatchingSummaryAlerts();
    const isEquivalentProductMatchingWithFunctionsEnabled = retailerSettings?.enableMatching === MATCHING.RICARDO;
    const { MessageWithAlerts } = useEquivalentProductMatchingSummaryAlerts(
        candidateColor && isEquivalentProductMatchingWithFunctionsEnabled
            ? { color: candidateColor }
            : matchingCheckSkipToken
    );

    const requestedColor = useAppSelector(selectRequestedColor);

    const { currPage, setCurrPage, pageSize, setPageSize, pageSizeOptions, filterItemsByPage, resetCurrPage } =
        usePagination({});

    const { trackSelectColor } = useAnalyticsEvents();

    const handleViewSlabClick = (color: ColorModel) => {
        setIsSlabViewerVisible(true);
        setPreviewColor(color);
    };

    useEffect(() => {
        resetCurrPage(); // Reset the pagination if the material changes
    }, [selectedMaterial, resetCurrPage]);

    const getDescription = (colors: ColorModel[]): string =>
        colors.length ? t('SCROLLABLE_AREA.COLOR.DESCRIPTION') : t('SCROLLABLE_AREA.COLOR.UNAVAILABLE');

    const setSortingOption = (e: MenuInfo) => {
        dispatch(updateColorSortBy(e.key as unknown as SortBy));
        resetCurrPage();
    };

    const tryChangeColor = async (newColor: ColorModel): Promise<void> => {
        if (!!selectedColor && newColor.productId === selectedColor?.productId) {
            return;
        }
        if (isEquivalentProductMatchingWithFunctionsEnabled) {
            await replaceIfNoAlerts({
                targetParameters: { color: newColor },
                onAlertsCallback: () => {
                    setCandidateColor(newColor);
                    setIsModalVisible(true);
                    dispatch(resetRequestedColor());
                },
                onNoAlertsCallback: () => {
                    trackSelectColor({ color: newColor });
                    setSelectedColorWithHistory(newColor);
                    dispatch(resetRequestedColor());
                },
            });
        } else {
            trackSelectColor({ color: newColor });
            setSelectedColorWithHistory(newColor);
            dispatch(resetRequestedColor());
        }
    };

    const onCancelChangingColor = () => {
        setIsModalVisible(false);
        dispatch(resetRequestedColor());
    };

    const onConfirmChangeColor = async () => {
        if (!candidateColor) {
            return;
        }
        if (isEquivalentProductMatchingWithFunctionsEnabled) {
            await replaceProductsWithEquivalents({ color: candidateColor });
        }
        trackSelectColor({ color: candidateColor });
        setSelectedColorWithHistory(candidateColor);
        setIsModalVisible(false);
    };

    //externally requested material and color change
    useEffect(() => {
        if (requestedColor?.material || !requestedColor?.color) {
            // if there's a material change, let materials handle it
            return;
        }
        tryChangeColor(requestedColor?.color);
        dispatch(resetRequestedColor());
    }, [dispatch, requestedColor, tryChangeColor, setSelectedColorWithHistory]);

    if (!zipcode || !selectedStore || !selectedMaterial) {
        return (
            <CardItem
                id={step}
                title={t('SCROLLABLE_AREA.COLOR.TITLE')}
                description={t('COMMON.NO_ZIP_CODE_PROCEED')}
                collapsed
                disabled={disabled}
            />
        );
    }

    return (
        <>
            <LoaderOnAlerts />
            <CardItem
                title={t('SCROLLABLE_AREA.COLOR.TITLE')}
                id={step}
                stepInfo={stepInfo}
                description={getDescription(colors)}
                disabled={disabled}
                // TODO: check if 'colors' can be removed since there are already 'filteredColors'
                extra={
                    zipcode && colors && colors.length ? (
                        <SortByMenu
                            sortBy={sortBy}
                            setSortingOption={setSortingOption}
                            options={[
                                SortBy.TrendingNow,
                                SortBy.LowToHigh,
                                SortBy.HighToLow,
                                SortBy.NameAZ,
                                SortBy.NameZA,
                            ]}
                        />
                    ) : null
                }
            >
                {colors.length ? (
                    <>
                        <div
                            className={classNames('color-card-wrapper', {
                                'row-direction': isFilterExpanded,
                            })}
                        >
                            <Filters
                                isFilterExpanded={isFilterExpanded}
                                setFilterExpanded={setIsFilterExpanded}
                                filterOptions={{ ...colorFilters }}
                                updateFilters={(filterOptions) => {
                                    updateFiltersOptions({ ...filterOptions }, colors, (updatedColorFilters) => {
                                        dispatch(updateColorFilters(updatedColorFilters));
                                    });
                                    resetCurrPage();
                                }}
                            />
                            <Row className="card-colors" gutter={[10, 10]} justify="space-around">
                                {filterItemsByPage(sortColors(filteredColors, sortBy)).map((color) => (
                                    <Col className="color-card-item" key={color.productId}>
                                        <ColorCard
                                            discountPercentage={color.discountPercentage}
                                            hasVisualizer={!!retailerFileSettings?.showVisualizer}
                                            imageSrc={ImageUtils.imgUrl(color.swatchImageUrl, 300)}
                                            onTrendLabel={getOnTrendLabelFromProduct(color)}
                                            isSelected={selectedColor?.productId === color?.productId}
                                            onClick={() => tryChangeColor(color)}
                                            onViewSlabClick={() => handleViewSlabClick(color)}
                                            overlayColor={color.overlayColor}
                                            price={color.price}
                                            priceWithoutDiscount={color.priceWithoutDiscount}
                                            title={`${getProductDescription(color)}${
                                                color.thicknessOptions.length > 1 ? ` - ${color.thickness}` : ''
                                            }`}
                                            visualizerSku={getColorRoomvoSku(color)}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        </div>
                        <Row justify="center" className={'color-pagination'}>
                            <Col>
                                <Pagination
                                    current={currPage}
                                    total={filteredColors.length}
                                    pageSizeLabel={t('SCROLLABLE_AREA.COLOR.PAGE_SIZE_LABEL')}
                                    pageSize={pageSize}
                                    pageSizeOptions={pageSizeOptions}
                                    onChange={(newPageNumber) => setCurrPage(newPageNumber)}
                                    onPageSizeChange={(newPageSize) => {
                                        setPageSize(newPageSize);
                                        resetCurrPage();
                                    }}
                                />
                            </Col>
                        </Row>
                    </>
                ) : null}
            </CardItem>
            {isEquivalentProductMatchingWithFunctionsEnabled ? (
                <ConfirmationDialogWithBanner
                    visible={isModalVisible}
                    banner={
                        <DialogBanner
                            icon={<VisualizeIcon />}
                            bannerText={t('EQUIVALENT_PRODUCT_MATCHING.ALERTS_TEXT')}
                        />
                    }
                    onCancel={onCancelChangingColor}
                    onConfirm={onConfirmChangeColor}
                    title={t('SCROLLABLE_AREA.COLOR.MODAL_TITLE', {
                        newColor: candidateColor?.name,
                    })}
                    content={<MessageWithAlerts />}
                />
            ) : null}
            {!isEquivalentProductMatchingWithFunctionsEnabled && (
                <ConfirmationDialog
                    visible={isModalVisible}
                    onCancel={onCancelChangingColor}
                    onConfirm={onConfirmChangeColor}
                    icon={<VisualizeIcon />}
                    title={t('SCROLLABLE_AREA.COLOR.MODAL_TITLE', {
                        newColor: candidateColor?.name,
                    })}
                    content={t('SCROLLABLE_AREA.MATERIAL.MATERIAL_CHANGE_WARNING')}
                />
            )}
            {previewColor ? (
                <SlabViewer
                    visible={isSlabViewerVisible}
                    previewColor={previewColor}
                    materialName={selectedMaterial?.description ?? ''}
                    onCancel={() => setIsSlabViewerVisible(false)}
                />
            ) : null}
        </>
    );
};

export default Colors;
